import { TrendingUp, Calculator, Award } from 'lucide-react';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { MetricLabel } from './MetricLabel';
import { SectionTitle, GoldBadge } from '../Luxury';

export interface EquityInProps {
  downPayment: number;
  closingCosts: number;
  capEx: number;
  constructionCarry?: number;
  total: number;
}

export interface ExitAnalysisProps {
  saleProceeds: number;
  loanPayoff: number;
  sellingCosts: number;
  sellingCostPct?: number;
  netEquity: number;
  equityMultiple: number;
  irr: number;
  /** Cumulative tax savings from depreciation (bonus + standard) over hold period */
  cumulativeTaxSavings?: number;
  /** After-tax equity multiple (includes cumulative cash flow + tax benefits) */
  afterTaxEquityMultiple?: number;
  /** After-tax IRR (includes depreciation tax benefits) */
  afterTaxIrr?: number;
  /** Cumulative after-tax cash flow over hold period */
  cumulativeAfterTaxCF?: number;
  // Exit tax breakdown (Phase 192)
  exitRecaptureTax?: number;       // 25% depreciation recapture
  exitCapitalGainsTax?: number;    // 23.8% capital gains
  exitStateTax?: number;           // 3.07% PA state tax
  exitTotalTaxes?: number;         // Total exit taxes
  exitDeferredTaxes?: number;      // 1031 deferred amount
  exitStrategy?: 'clean' | '1031';
}

interface EquityInOutProps {
  equityIn: EquityInProps;
  fiveYearExit: ExitAnalysisProps;
  tenYearExit: ExitAnalysisProps;
  className?: string;
}

/**
 * EquityInOut component - Luxury equity in/out analysis with 5yr vs 10yr comparison.
 *
 * Features:
 * - Equity invested breakdown card (dark charcoal background)
 * - Side-by-side 5-Year vs 10-Year exit comparison cards
 * - Sale proceeds, loan payoff, selling costs, net equity, equity multiple, IRR
 * - Amber callout with equity multiple calculation breakdown
 * - GoldBadge recommending best exit based on IRR comparison
 * - Highlight variant for equity multiple > 2.0x
 * - JetBrains Mono for all dollar amounts (font-mono)
 * - Emerald for positive equity, crimson for deductions
 */
export function EquityInOut({
  equityIn,
  fiveYearExit,
  tenYearExit,
  className = '',
}: EquityInOutProps) {
  // Determine best exit based on IRR
  const recommendedExit = fiveYearExit.irr >= tenYearExit.irr ? 5 : 10;

  // Check if equity multiples exceed threshold for highlight
  const fiveYearHighlight = fiveYearExit.equityMultiple >= 2.0;
  const tenYearHighlight = tenYearExit.equityMultiple >= 2.0;

  // Use 5-year for primary breakdown calculation display
  const primaryExit = fiveYearExit;

  // After-tax highlight thresholds (kept for potential future use)
  void ((fiveYearExit.afterTaxEquityMultiple ?? 0) >= 2.0);
  void ((tenYearExit.afterTaxEquityMultiple ?? 0) >= 2.0);

  // Determine recommended exit based on after-tax IRR when available, fallback to pre-tax
  void ((fiveYearExit.afterTaxIrr ?? 0) >= (tenYearExit.afterTaxIrr ?? 0) ? 5 : 10);

  /**
   * Renders the exit card content (shared between 5yr and 10yr).
   * Shows pre-tax waterfall, then after-tax section with cumulative tax savings.
   */
  function renderExitCard(
    exit: ExitAnalysisProps,
    years: number,
    isHighlight: boolean,
    _isRecommended: boolean,
    totalEquityIn: number,
  ) {
    const hasAfterTax = exit.cumulativeTaxSavings != null && exit.cumulativeTaxSavings > 0;
    void (hasAfterTax
      ? exit.netEquity + (exit.cumulativeAfterTaxCF != null
          ? (exit.cumulativeAfterTaxCF + totalEquityIn) // cumulative CF already includes Year 0 negative
          : exit.cumulativeTaxSavings!)
      : exit.netEquity);

    return (
      <div className="space-y-2 text-sm">
        {/* Pre-tax waterfall */}
        <div className="flex justify-between items-center py-1">
          <span className="text-ivory/70">Sale Proceeds</span>
          <span className="font-mono tabular-nums text-ivory">
            {formatCurrency(exit.saleProceeds)}
          </span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-ivory/70">Less: Loan Payoff</span>
          <span className="font-mono tabular-nums text-crimson">
            ({formatCurrency(exit.loanPayoff)})
          </span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-ivory/70">Less: Selling Costs ({((exit.sellingCostPct ?? 0.05) * 100).toFixed(0)}%)</span>
          <span className="font-mono tabular-nums text-crimson">
            ({formatCurrency(exit.sellingCosts)})
          </span>
        </div>

        <div className="border-t border-white/20 my-2"></div>

        {/* Net Equity Out (pre-tax) */}
        <div className="flex justify-between items-center py-2">
          <span className="text-ivory font-semibold">Net Equity Out</span>
          <span className="font-mono tabular-nums text-lg font-bold text-emerald">
            {formatCurrency(exit.netEquity)}
          </span>
        </div>

        {/* Pre-tax Equity Multiple & IRR */}
        <div className="flex justify-between items-center py-1">
          <span className="text-ivory/70"><MetricLabel metric="equity_multiple">Equity Multiple</MetricLabel></span>
          <span className={`font-mono tabular-nums font-bold text-lg ${isHighlight ? 'text-gold' : 'text-ivory'}`}>
            {exit.equityMultiple.toFixed(2)}x
          </span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-ivory/70"><MetricLabel metric="irr">IRR</MetricLabel></span>
          <span className="font-mono tabular-nums text-ivory">
            {formatPercent(exit.irr, 1)}
          </span>
        </div>

        {/* After-Tax Section */}
        {hasAfterTax && (
          <>
            <div className="border-t border-amber-500/30 my-3"></div>
            <div className="bg-amber-900/15 border border-amber-600/20 rounded-lg p-3 space-y-2">
              <p className="text-sm uppercase tracking-[0.15em] text-amber-400 font-semibold mb-2">
                After-Tax Returns (incl. Depreciation)
              </p>

              {/* Cumulative Tax Savings */}
              <div className="flex justify-between items-center py-1">
                <span className="text-amber-200/70 text-sm">
                  Cumulative Tax Savings ({years}yr)
                </span>
                <span className="font-mono tabular-nums text-amber-300 font-semibold">
                  +{formatCurrency(exit.cumulativeTaxSavings!)}
                </span>
              </div>

              {/* After-Tax Equity Multiple */}
              {exit.afterTaxEquityMultiple != null && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-amber-200/70 text-sm">After-Tax Equity Multiple</span>
                  <span className={`font-mono tabular-nums font-bold text-lg ${
                    exit.afterTaxEquityMultiple >= 2.0 ? 'text-gold' : 'text-amber-300'
                  }`}>
                    {exit.afterTaxEquityMultiple.toFixed(2)}x
                  </span>
                </div>
              )}

              {/* After-Tax IRR */}
              {exit.afterTaxIrr != null && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-amber-200/70 text-sm"><MetricLabel metric="after_tax_irr">After-Tax IRR</MetricLabel></span>
                  <span className={`font-mono tabular-nums font-bold ${
                    exit.afterTaxIrr >= 0.15 || (exit.afterTaxIrr > 1 && exit.afterTaxIrr >= 15)
                      ? 'text-emerald' : 'text-amber-300'
                  }`}>
                    {formatPercent(exit.afterTaxIrr, 1)}
                  </span>
                </div>
              )}

              {/* IRR boost from tax benefits */}
              {exit.afterTaxIrr != null && exit.irr > 0 && (() => {
                const afterTaxPct = exit.afterTaxIrr > 1 ? exit.afterTaxIrr : exit.afterTaxIrr * 100;
                const preTaxPct = exit.irr > 1 ? exit.irr : exit.irr * 100;
                const boost = afterTaxPct - preTaxPct;
                if (boost <= 0) return null;
                return (
                  <div className="flex justify-between items-center py-1">
                    <span className="text-amber-200/50 text-sm">Tax Benefit IRR Boost</span>
                    <span className="font-mono tabular-nums text-sm text-amber-400">
                      +{boost.toFixed(2)}pp
                    </span>
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {/* Exit Tax Breakdown */}
        {(exit.exitTotalTaxes ?? 0) > 0 && (
          <>
            <div className="border-t border-crimson/20 my-3"></div>
            <div className="bg-charcoal border border-crimson/20 rounded-lg p-3 space-y-2">
              {exit.exitStrategy === '1031' ? (
                <>
                  <p className="text-sm uppercase tracking-[0.15em] text-emerald font-semibold mb-2">
                    1031 Exchange — Taxes Deferred
                  </p>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-emerald/70 text-sm">Taxes Deferred via 1031 Exchange</span>
                    <span className="font-mono tabular-nums text-emerald font-bold">
                      {formatCurrency(exit.exitDeferredTaxes ?? exit.exitTotalTaxes!)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm uppercase tracking-[0.15em] text-crimson/80 font-semibold mb-2">
                    Exit Tax Breakdown
                  </p>
                  {(exit.exitRecaptureTax ?? 0) > 0 && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-ivory/60 text-sm">Depreciation Recapture (25%)</span>
                      <span className="font-mono tabular-nums text-crimson text-sm">
                        ({formatCurrency(exit.exitRecaptureTax!)})
                      </span>
                    </div>
                  )}
                  {(exit.exitCapitalGainsTax ?? 0) > 0 && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-ivory/60 text-sm">Capital Gains (23.8%)</span>
                      <span className="font-mono tabular-nums text-crimson text-sm">
                        ({formatCurrency(exit.exitCapitalGainsTax!)})
                      </span>
                    </div>
                  )}
                  {(exit.exitStateTax ?? 0) > 0 && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-ivory/60 text-sm">PA State Tax (3.07%)</span>
                      <span className="font-mono tabular-nums text-crimson text-sm">
                        ({formatCurrency(exit.exitStateTax!)})
                      </span>
                    </div>
                  )}
                  <div className="border-t border-crimson/15 my-1"></div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-ivory/80 text-sm font-semibold">Total Exit Taxes</span>
                    <span className="font-mono tabular-nums text-crimson font-bold">
                      ({formatCurrency(exit.exitTotalTaxes!)})
                    </span>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-obsidian rounded-xl p-6 ${className}`}>
      <SectionTitle icon={<TrendingUp className="w-5 h-5" />}>
        Equity In/Out Analysis
      </SectionTitle>

      {/* Section 1: Equity Invested Card */}
      <div className="bg-charcoal border border-slate-dark rounded-lg p-5 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gold/10 rounded-lg">
            <Calculator className="w-4 h-4 text-gold" />
          </div>
          <h4 className="text-sm uppercase tracking-[0.15em] text-stone-700 font-semibold">
            Equity Invested
          </h4>
        </div>

        {/* Breakdown list */}
        <div className="space-y-3 text-sm mb-4">
          <div className="flex justify-between items-center py-1 border-b border-white/10">
            <span className="text-ivory/70">Down Payment</span>
            <span className="font-mono tabular-nums text-ivory">
              {formatCurrency(equityIn.downPayment)}
            </span>
          </div>
          <div className="flex justify-between items-center py-1 border-b border-white/10">
            <span className="text-ivory/70">Closing Costs</span>
            <span className="font-mono tabular-nums text-ivory">
              {formatCurrency(equityIn.closingCosts)}
            </span>
          </div>
          {equityIn.capEx > 0 && (
            <div className="flex justify-between items-center py-1 border-b border-white/10">
              <span className="text-ivory/70">CapEx (cash)</span>
              <span className="font-mono tabular-nums text-ivory">
                {formatCurrency(equityIn.capEx)}
              </span>
            </div>
          )}
          {(equityIn.constructionCarry ?? 0) > 0 && (
            <div className="flex justify-between items-center py-1 border-b border-white/10">
              <span className="text-ivory/70">Construction Carry</span>
              <span className="font-mono tabular-nums text-ivory">
                {formatCurrency(equityIn.constructionCarry!)}
              </span>
            </div>
          )}
        </div>

        {/* Total equity invested */}
        <div className="flex justify-between items-center py-3 border-t-2 border-gold/30">
          <span className="text-gold font-semibold">Total Equity Invested</span>
          <span className="font-mono tabular-nums text-lg font-bold text-gold">
            {formatCurrency(equityIn.total)}
          </span>
        </div>
      </div>

      {/* Section 2: Exit Comparison Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* 5-Year Exit Card */}
        <div
          className={`
            border rounded-lg p-5 transition-all
            ${fiveYearHighlight
              ? 'border-gold bg-gradient-to-br from-gold/10 to-gold/5'
              : 'border-slate-dark bg-charcoal'
            }
          `}
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm uppercase tracking-[0.15em] text-ivory/80 font-semibold">
              5-Year Exit
            </h4>
            {recommendedExit === 5 && (
              <GoldBadge variant="gold">Recommended</GoldBadge>
            )}
          </div>

          {renderExitCard(fiveYearExit, 5, fiveYearHighlight, recommendedExit === 5, equityIn.total)}
        </div>

        {/* 10-Year Exit Card */}
        <div
          className={`
            border rounded-lg p-5 transition-all
            ${tenYearHighlight
              ? 'border-gold bg-gradient-to-br from-gold/10 to-gold/5'
              : 'border-slate-dark bg-charcoal'
            }
          `}
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm uppercase tracking-[0.15em] text-ivory/80 font-semibold">
              10-Year Exit
            </h4>
            {recommendedExit === 10 && (
              <GoldBadge variant="gold">Recommended</GoldBadge>
            )}
          </div>

          {renderExitCard(tenYearExit, 10, tenYearHighlight, recommendedExit === 10, equityIn.total)}
        </div>
      </div>

      {/* Section 3: Equity Multiple Calculation Breakdown (Amber Callout) */}
      <div className="bg-amber-900/20 border border-amber-600/40 rounded-lg p-5 mb-6">
        <div className="font-semibold text-sm uppercase tracking-[0.1em] text-amber-400 mb-4">
          Equity Multiple Calculation &mdash; How {primaryExit.equityMultiple.toFixed(2)}x is calculated:
        </div>

        <div className="space-y-2 text-sm">
          {/* Step 1 */}
          <div className="flex justify-between items-center py-1">
            <span className="text-amber-200/80">
              <span className="text-amber-400 font-semibold">Step 1:</span> Net Equity Out
            </span>
            <span className="font-mono tabular-nums text-amber-100">
              {formatCurrency(primaryExit.netEquity)}
            </span>
          </div>

          {/* Step 2 */}
          <div className="flex justify-between items-center py-1">
            <span className="text-amber-200/80">
              <span className="text-amber-400 font-semibold">Step 2:</span> &divide; Equity In
            </span>
            <span className="font-mono tabular-nums text-amber-100">
              {formatCurrency(equityIn.total)}
            </span>
          </div>

          {/* Divider */}
          <div className="border-t border-amber-600/40 my-2"></div>

          {/* Step 3 - Result */}
          <div className="flex justify-between items-center py-2">
            <span className="text-amber-300 font-semibold">
              <span className="text-amber-400 font-semibold">Step 3:</span> = Equity Multiple
            </span>
            <span className="font-mono tabular-nums text-lg font-bold text-amber-300">
              {primaryExit.equityMultiple.toFixed(2)}x
            </span>
          </div>
        </div>

        <div className="mt-3 text-sm text-amber-400/70">
          Based on 5-year hold. Net Equity Out = Sale Proceeds - Loan Payoff - Selling Costs.
        </div>
      </div>

      {/* Section 4: Recommended Exit Badge */}
      <div className="flex items-center justify-center gap-3 p-4 bg-charcoal border border-slate-dark rounded-lg">
        <Award className="w-5 h-5 text-gold" />
        <GoldBadge variant="gold">
          Recommended: {recommendedExit}-Year Exit
        </GoldBadge>
        <span className="text-ivory/60 text-sm">
          (Higher IRR: {formatPercent(recommendedExit === 5 ? fiveYearExit.irr : tenYearExit.irr, 1)})
        </span>
      </div>
    </div>
  );
}
